"use client";

import { useState, useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { Menu, X, Search, Globe, User, LogOut } from "lucide-react";
import { BrandLogo } from "@/components/brand/brand-mark";
import { Button } from "@/components/ui/button";

const LOCALES = [
  { code: "en", label: "English", flag: "🇨🇦" },
  { code: "es", label: "Español", flag: "🇲🇽" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

/** Glass pill nav. `overlay`: fixed on top of the home hero instead of sticky. */
export function Navbar({ overlay = false }: { overlay?: boolean }) {
  const t = useTranslations("nav");
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [client, setClient] = useState<{ name: string; email: string } | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetch("/api/client/me").then(r => {
      if (r.ok) r.json().then(d => setClient(d.user));
    });
  }, []);

  function switchLocale(newLocale: string) {
    router.replace(pathname, { locale: newLocale });
    setLangOpen(false);
    setMobileOpen(false);
  }

  const currentLocale = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  return (
    <header className={`${overlay ? "fixed" : "sticky"} top-0 z-50 w-full border-b transition-all duration-300 ${scrolled || mobileOpen ? "border-white/10 bg-[#05070f]/80 py-1 backdrop-blur-md" : "border-transparent pt-4"}`}>
      <div className="max-w-[1600px] mx-auto px-4 md:px-[4%] h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex shrink-0 items-center mr-6" aria-label="ShapePrint 3D">
          <BrandLogo className="h-9 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1 whitespace-nowrap rounded-full border border-white/20 bg-white/[0.08] p-1.5 text-sm text-white/75 shadow-[0_8px_32px_rgba(0,0,0,0.1)] backdrop-blur-xl [&>a]:rounded-full [&>a]:px-3 2xl:[&>a]:px-4 [&>a]:py-1.5 [&>a]:transition-colors [&>a:hover]:bg-orange-300 [&>a:hover]:text-zinc-950 [&>a[aria-current=page]]:bg-orange-300 [&>a[aria-current=page]]:text-zinc-950">
          <Link href="/#como-funciona">{t("howItWorks")}</Link>
          <Link href="/#servicios">{t("services")}</Link>
          <Link href="/shop" aria-current={pathname === "/shop" ? "page" : undefined}>{t("shop")}</Link>
          <Link href="/upload" aria-current={pathname === "/upload" ? "page" : undefined}>{t("uploadModel")}</Link>
          <Link href="/#materiales">{t("materials")}</Link>
        </nav>

        {/* Desktop actions */}
        <div className="hidden xl:flex items-center gap-2 whitespace-nowrap">
          {/* Account */}
          {client ? (
            <Link href="/account/orders" aria-label={client.name} title={client.name} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-100 hover:bg-white/10 transition-colors">
              <User className="h-4 w-4" />
              <span className="hidden 2xl:inline">{client.name.split(" ")[0]}</span>
            </Link>
          ) : (
            <Link href="/account/login" aria-label={t("account")} title={t("account")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-100 hover:bg-white/10 transition-colors">
              <User className="h-4 w-4" />
              <span className="hidden 2xl:inline">{t("account") || "Cuenta"}</span>
            </Link>
          )}

          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-zinc-300 hover:text-zinc-100 hover:bg-white/10 transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span>{currentLocale.flag} {currentLocale.code.toUpperCase()}</span>
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                <div className="absolute right-0 mt-2 w-40 rounded-2xl border border-white/10 bg-[#0d1530]/95 backdrop-blur-xl shadow-lg z-20 overflow-hidden">
                  {LOCALES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => switchLocale(l.code)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-zinc-800 transition-colors text-left ${l.code === locale ? "text-(--accent) bg-zinc-800/50" : "text-zinc-300"}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.label}</span>
                      {l.code === locale && <span className="ml-auto text-xs">✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <Link href="/tracking">
            <Button variant="outline" size="sm" className="gap-2">
              <Search className="h-3.5 w-3.5" />{t("trackOrder")}
            </Button>
          </Link>
          <Link href="/order/new">
            <Button size="sm">{t("placeOrder")}</Button>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button className="xl:hidden p-2 text-zinc-400 hover:text-zinc-100" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="xl:hidden border-t border-white/10 bg-[#05070f]/95 backdrop-blur-xl px-4 py-4 space-y-3">
          <Link href="/#como-funciona" className="block text-zinc-400 hover:text-zinc-100 py-2" onClick={() => setMobileOpen(false)}>{t("howItWorks")}</Link>
          <Link href="/#servicios" className="block text-zinc-400 hover:text-zinc-100 py-2" onClick={() => setMobileOpen(false)}>{t("services")}</Link>
          <Link href="/shop" className="block text-zinc-400 hover:text-zinc-100 py-2" onClick={() => setMobileOpen(false)}>{t("shop")}</Link>
          <Link href="/upload" className="block text-zinc-400 hover:text-zinc-100 py-2" onClick={() => setMobileOpen(false)}>{t("uploadModel")}</Link>
          <Link href="/#materiales" className="block text-zinc-400 hover:text-zinc-100 py-2" onClick={() => setMobileOpen(false)}>{t("materials")}</Link>

          {/* Mobile language switcher */}
          <div className="py-2 border-t border-zinc-800">
            <p className="text-xs text-zinc-600 mb-2">Language</p>
            <div className="flex gap-2">
              {LOCALES.map((l) => (
                <button key={l.code} onClick={() => switchLocale(l.code)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${l.code === locale ? "bg-(--accent)/15 text-(--accent)" : "bg-zinc-800 text-zinc-400"}`}>
                  {l.flag} {l.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Link href="/tracking" onClick={() => setMobileOpen(false)}>
              <Button variant="outline" size="sm" className="w-full gap-2">
                <Search className="h-3.5 w-3.5" />{t("trackOrder")}
              </Button>
            </Link>
            <Link href="/order/new" onClick={() => setMobileOpen(false)}>
              <Button size="sm" className="w-full">{t("placeOrder")}</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
